// RetainerModal — extracted from AccountingPage.js (Feb-2026 monolith split).
// Pure form, zero closures over the parent — all deps flow via props.
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

export default function RetainerModal({ isOpen, onClose, client, onSave }) {
  const [formData, setFormData] = useState({
    retainer_value: 0,
    billing_cycle: 'monthly',
    payment_type: 'postpaid',
    start_date: '',
    end_date: '',
    is_active: true,
    company_name: '',
    company_address: '',
    gstin: '',
    pan: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (client) {
      const retainerValue = client.retainer_value || client.retainer || 0;
      const isActive = client.retainer_is_active !== false && retainerValue > 0 ? true : (retainerValue > 0);
      setFormData({
        retainer_value: retainerValue,
        billing_cycle: client.retainer_billing_cycle || client.billing_cycle || 'monthly',
        payment_type: client.payment_type || 'postpaid',
        start_date: client.retainer_start_date || '',
        end_date: client.retainer_end_date || '',
        is_active: isActive,
        company_name: client.company_name || client.name || '',
        company_address: client.company_address || '',
        gstin: client.gstin || '',
        pan: client.pan || '',
      });
    }
  }, [client]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSave(client._id, formData);
      onClose();
    } catch (error) {
      console.error('Save error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Retainer - {client?.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Monthly Retainer Value (INR)</label>
            <Input
              type="number"
              value={formData.retainer_value}
              onChange={(e) => setFormData((prev) => ({ ...prev, retainer_value: parseFloat(e.target.value) || 0 }))}
              placeholder="Enter monthly retainer amount"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Billing Cycle</label>
              <Select value={formData.billing_cycle} onValueChange={(v) => setFormData((prev) => ({ ...prev, billing_cycle: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Payment Type</label>
              <Select value={formData.payment_type} onValueChange={(v) => setFormData((prev) => ({ ...prev, payment_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="advance">Advance</SelectItem>
                  <SelectItem value="postpaid">Postpaid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Start Date</label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData((prev) => ({ ...prev, start_date: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">End Date</label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData((prev) => ({ ...prev, end_date: e.target.value }))}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
              className="rounded border-white/40"
            />
            <label htmlFor="is_active" className="text-sm text-neutral-700">Active Retainer</label>
          </div>

          <div className="border-t border-white/40 pt-4 mt-4">
            <h4 className="text-sm font-semibold text-[#1a1a2e] mb-3">Company Details (For Invoicing)</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Legal Company Name</label>
                <Input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, company_name: e.target.value }))}
                  placeholder="Enter legal company name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Company Address</label>
                <textarea
                  value={formData.company_address}
                  onChange={(e) => setFormData((prev) => ({ ...prev, company_address: e.target.value }))}
                  className="w-full px-3 py-2 border border-white/40 rounded-xl text-sm"
                  placeholder="Enter company address"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">GSTIN</label>
                  <Input
                    type="text"
                    value={formData.gstin}
                    onChange={(e) => setFormData((prev) => ({ ...prev, gstin: e.target.value.toUpperCase() }))}
                    placeholder="e.g., 22AAAAA0000A1Z5"
                    maxLength={15}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">PAN</label>
                  <Input
                    type="text"
                    value={formData.pan}
                    onChange={(e) => setFormData((prev) => ({ ...prev, pan: e.target.value.toUpperCase() }))}
                    placeholder="e.g., AAAAA0000A"
                    maxLength={10}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving...' : 'Save Retainer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
